'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock3, Loader2, Package } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppointmentLiveTimers } from '@/hooks/useAppointmentLiveTimers';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import type { AppointmentAssigneeWorkflowWorkItem } from './AppointmentAssigneeWorkflowSection';
import BillingCommentsSection from './BillingCommentsSection';
import JobExpensesTab from './JobExpensesTab';
import TimeTrackingTab from './TimeTrackingTab';
import {
  getTimerAggregationModeFromCustomFields,
  loadTimerAggregationMode,
  persistTimerAggregationModeForWorkItem,
  saveTimerAggregationMode,
} from './billingDashboardUtils';

export type AppointmentBillingSectionId = 'time' | 'billing';

interface BomItem {
  id: string;
  item_name?: string;
  description?: string;
  item_code?: string;
  quantity: number;
  unit: string;
  unit_cost?: number;
  line_total?: number;
  line_type?: string;
}

type AppointmentBillingWorkItem = AppointmentAssigneeWorkflowWorkItem & {
  item_number?: string;
  title?: string;
  item_type?: string;
  contact_id?: number;
  contact_name?: string;
};

interface AppointmentBillingTabProps {
  workItemId: string;
  workItem?: AppointmentBillingWorkItem;
  isActive?: boolean;
  readOnly?: boolean;
  timerEditAccess?: boolean;
  defaultSection?: AppointmentBillingSectionId;
  onExpensesChange?: (count: number) => void;
  onWorkItemPatched?: (updates: { status?: string; custom_fields?: Record<string, unknown> }) => void;
}

const SECTION_COPY: Record<AppointmentBillingSectionId, { label: string; icon: React.ElementType }> = {
  time: {
    label: 'Time & timers',
    icon: Clock3,
  },
  billing: {
    label: 'Billing & expenses',
    icon: Package,
  },
};

export default function AppointmentBillingTab({
  workItemId,
  workItem,
  isActive = true,
  readOnly = false,
  timerEditAccess = true,
  defaultSection = 'time',
  onExpensesChange,
  onWorkItemPatched,
}: AppointmentBillingTabProps) {
  const { currentTheme, clientBranding } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const accentColor = clientBranding?.primary_color || currentTheme.colors.primary;

  const [activeSection, setActiveSection] = useState<AppointmentBillingSectionId>(defaultSection);
  const [timeComposerToken] = useState(0);
  const [expenseComposerToken] = useState(0);
  const [bomItems, setBomItems] = useState<BomItem[]>([]);
  const [loadingBom, setLoadingBom] = useState(false);
  const [hasLoadedBom, setHasLoadedBom] = useState(false);
  const [timerAggregationMode, setTimerAggregationMode] = useState<'individual' | 'team'>(loadTimerAggregationMode);
  const timerModeUserToggledRef = useRef(false);

  // Prefer the mode persisted on this appointment over the browser-global
  // default; an explicit toggle in this session wins over late-arriving props.
  const storedTimerAggregationMode = getTimerAggregationModeFromCustomFields(workItem?.custom_fields);

  useEffect(() => {
    timerModeUserToggledRef.current = false;
  }, [workItemId]);

  useEffect(() => {
    if (!storedTimerAggregationMode || timerModeUserToggledRef.current) return;
    setTimerAggregationMode((current) => current === storedTimerAggregationMode ? current : storedTimerAggregationMode);
  }, [storedTimerAggregationMode]);

  const handleTimerAggregationModeChange = useCallback((mode: 'individual' | 'team') => {
    timerModeUserToggledRef.current = true;
    setTimerAggregationMode(mode);
    // Persist onto the work item so generated documents and other sessions
    // resolve the same per-appointment setting.
    persistTimerAggregationModeForWorkItem(workItemId, mode);
    const existingFields = workItem?.custom_fields && typeof workItem.custom_fields === 'object'
      ? workItem.custom_fields
      : {};
    onWorkItemPatched?.({
      custom_fields: { ...existingFields, timer_aggregation_mode: mode },
    });
  }, [workItemId, workItem?.custom_fields, onWorkItemPatched]);

  useEffect(() => {
    saveTimerAggregationMode(timerAggregationMode);
  }, [timerAggregationMode]);

  useEffect(() => {
    setActiveSection(defaultSection);
  }, [defaultSection]);

  const assigneeCount = useMemo(() => {
    if (Array.isArray(workItem?.assignees) && workItem.assignees.length > 0) {
      return workItem.assignees.length;
    }
    return workItem?.assigned_to ? 1 : 0;
  }, [workItem?.assignees, workItem?.assigned_to]);

  const showTeamToggle = assigneeCount > 1;

  const loadBomItems = useCallback(async () => {
    setLoadingBom(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        setHasLoadedBom(true);
        return;
      }
      const res = await fetch(getApiUrl(`/api/v1/work-items/${workItemId}/bom`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const items = data?.data?.items ?? data?.data ?? data?.items ?? [];
        setBomItems(Array.isArray(items) ? items : []);
        setHasLoadedBom(true);
      } else {
        setBomItems([]);
        setHasLoadedBom(true);
      }
    } catch {
      setBomItems([]);
      setHasLoadedBom(true);
    } finally {
      setLoadingBom(false);
    }
  }, [workItemId]);

  useEffect(() => {
    if (isActive && (activeSection === 'billing') && !hasLoadedBom && !loadingBom) {
      loadBomItems();
    }
  }, [isActive, activeSection, hasLoadedBom, loadingBom, loadBomItems]);

  useEffect(() => {
    setBomItems([]);
    setHasLoadedBom(false);
  }, [workItemId]);

  const { getTimer: getAppointmentLiveTimer } = useAppointmentLiveTimers({ appointmentIds: [workItemId] });

  const effectiveWorkItem = useMemo<AppointmentBillingWorkItem>(() => {
    return {
      id: workItem?.id || workItemId,
      item_number: workItem?.item_number,
      title: workItem?.title,
      item_type: workItem?.item_type || 'appointment',
      contact_id: workItem?.contact_id,
      contact_name: workItem?.contact_name,
      status: workItem?.status,
      assigned_to: workItem?.assigned_to,
      assigned_to_name: workItem?.assigned_to_name,
      assignee_name: workItem?.assignee_name,
      custom_fields: workItem?.custom_fields,
      assignees: workItem?.assignees,
    };
  }, [workItem, workItemId]);

  const liveAppointmentTimer = getAppointmentLiveTimer(workItemId);

  /** Billing comments load once per tab while this workspace is active — keep visible across sub-tabs */
  const billingCommentsActive = Boolean(isActive && workItemId);

  return (
    <div className="space-y-4" data-tour-id="appointment-billing-tab">
      <div className={`overflow-hidden rounded-2xl border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className={`flex gap-1 border-b px-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {(Object.entries(SECTION_COPY) as Array<[AppointmentBillingSectionId, typeof SECTION_COPY[AppointmentBillingSectionId]]>).map(([sectionId, section]) => {
            const Icon = section.icon;
            const isSelected = sectionId === activeSection;
            return (
              <button
                key={sectionId}
                onClick={() => setActiveSection(sectionId)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  isSelected
                    ? 'text-blue-500'
                    : `${isDark ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`
                }`}
                style={isSelected ? { borderColor: accentColor, color: accentColor } : undefined}
              >
                <Icon className="w-3.5 h-3.5" />
                {section.label}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {activeSection === 'time' && (
            <>
              {showTeamToggle && (
                <div className={`flex items-center gap-2 mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="text-xs font-medium">Timer billing:</span>
                  <div className={`inline-flex rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
                    <button
                      type="button"
                      onClick={() => handleTimerAggregationModeChange('individual')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-l-lg transition-colors ${timerAggregationMode === 'individual'
                        ? (isDark ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700')
                        : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                        }`}
                    >
                      Individual
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTimerAggregationModeChange('team')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-r-lg transition-colors border-l ${isDark ? 'border-gray-700' : 'border-gray-200'} ${timerAggregationMode === 'team'
                        ? (isDark ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700')
                        : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                        }`}
                    >
                      Team
                    </button>
                  </div>
                  <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {timerAggregationMode === 'individual'
                      ? 'Each member billed separately'
                      : 'Team billed as one unit per day'}
                  </span>
                </div>
              )}
              <TimeTrackingTab
                workItemId={workItemId}
                includeRelated={false}
                isActive={isActive && activeSection === 'time'}
                isDark={isDark}
                readOnly={readOnly || !timerEditAccess}
                openComposerToken={timeComposerToken}
                liveTimer={liveAppointmentTimer}
                timerAggregationMode={timerAggregationMode}
              />
              <BillingCommentsSection
                workItemId={workItemId}
                isActive={billingCommentsActive}
                includeRelated={false}
                readOnly={readOnly}
                embedded
              />
            </>
          )}

          {activeSection === 'billing' && (
            <>
              {/* BOM / Billing Items */}
              {loadingBom ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : bomItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <Package className="w-10 h-10 mb-2 opacity-40" />
                  <p className="text-sm">No billing items added</p>
                  <p className="text-xs mt-1">Billing items are managed on the job billing workspace.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Billing Items ({bomItems.length})
                  </p>
                  {bomItems.map((item) => {
                    const unitCost = Number(item.unit_cost) || 0;
                    const lineTotal = item.line_total != null
                      ? Number(item.line_total)
                      : unitCost * item.quantity;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}
                      >
                        <div className={`p-1.5 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                          <Package className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {item.item_name || item.description || 'Unnamed item'}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {item.quantity} {item.unit}
                            </span>
                            {unitCost > 0 && (
                              <span className="text-[10px] text-gray-400">
                                @ R{unitCost.toFixed(2)}/{item.unit}
                              </span>
                            )}
                          </div>
                        </div>
                        {lineTotal > 0 && (
                          <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            R{lineTotal.toFixed(2)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Divider between billing items and expenses */}
              <div className={`my-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />

              {/* Expenses — all statuses shown, only approved are billable */}
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Expenses
              </p>
              <JobExpensesTab
                workItemId={workItemId}
                workItem={{
                  id: effectiveWorkItem.id,
                  item_number: effectiveWorkItem.item_number,
                  title: effectiveWorkItem.title,
                }}
                isActive={isActive && activeSection === 'billing'}
                readOnly={readOnly}
                openComposerToken={expenseComposerToken}
                onExpensesChange={onExpensesChange}
              />

              <BillingCommentsSection
                workItemId={workItemId}
                isActive={billingCommentsActive}
                includeRelated={false}
                readOnly={readOnly}
                embedded
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
